import { addHeader, addHeaderWithOutBody } from "./headers";
import constants from "./api_urls";
import { getclusterbyDomain } from "../services/white_label";
import { refreshTokenEventEmitter } from "./sessionEvents";
import { forceLogoutAndRedirect, API_ENDPOINTS } from "./httpClient";
import { rootStore } from "../store";

function getUser() {
  return rootStore?.UserStore?.userDetails || null;
}

const REFRESH_URL = constants.mainUrl + "/v1/auth/refresh/recruiter";
let isRefreshing = false;
let failedQueue = [];

/** Resolves when the in-flight refresh finishes (success or failure). On success callers retry; on failure redirect happens. */
async function doRefresh() {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }
  isRefreshing = true;
  try {
    const res = await fetch(REFRESH_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (res.status >= 200 && res.status < 300) {
      failedQueue.forEach((p) => p.resolve());
      failedQueue = [];
      isRefreshing = false;
      return;
    }
    const err = new Error("Refresh failed");
    failedQueue.forEach((p) => p.reject(err));
    failedQueue = [];
    isRefreshing = false;
    forceLogoutAndRedirect();
    throw err;
  } catch (err) {
    failedQueue.forEach((p) => p.reject(err));
    failedQueue = [];
    isRefreshing = false;
    forceLogoutAndRedirect();
    throw err;
  }
}

/** Fetch with one retry on 401 after refreshing. Skips refresh for login/refresh URLs. */
async function fetchWithAuthRetry(uri, init) {
  const isLoginOrRefresh = uri.includes("/refresh") || uri.includes("/login");
  if (isLoginOrRefresh) {
    return fetch(uri, init);
  }
  let res = await fetch(uri, init);
  if (res.status === 401 && !init._authRetry) {
    init._authRetry = true;
    await doRefresh();
    res = await fetch(uri, init);
  }
  return res;
}

export async function apiGet(path) {
  try {
    const uri = constants.mainUrl + path;
    const init = await addHeaderWithOutBody("GET");
    const response = await fetchWithAuthRetry(uri, init);
    console.log(response);
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    console.error("Fetch error:", error);
    return { body: null, status: 500, error: error.message };
  }
}

export async function apiGetPublic(path) {
  try {
    const response = await fetch(
      constants.mainUrlPublic + path,
      await addHeaderWithOutBody("GET"),
    );
    console.log(response);
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    console.error("Fetch error:", error);
    return { body: null, status: 500, error: error.message };
  }
}

export async function apiGetOpenSource(uriPath) {
  try {
    const response = await fetch(uriPath, await addHeaderWithOutBody("GET"));
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}

export async function apiDelete(path) {
  try {
    const uri = constants.mainUrl + path + `?editedBy=${getUser()?._id}`;
    const init = await addHeaderWithOutBody("DELETE");
    const response = await fetchWithAuthRetry(uri, init);
    console.log(response);
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}
export async function apiDeleteWithBody(body, path) {
  const uri = constants.mainUrl + path;
  try {
    const init = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(await addHeaderWithOutBody("DELETE")),
      },
      body: JSON.stringify(body),
      credentials: "include",
    };
    const response = await fetchWithAuthRetry(uri, init);
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}

export async function apiPostPut(body, path, method) {
  const uri = constants.mainUrl + path;
  console.log("oldBody", body);

  let newBody = body;
  if (method === "POST") {
    newBody = {
      ...newBody,
      addedBy: getUser()?._id,
      cluster: getclusterbyDomain(),
    };
  } else {
    newBody = {
      ...newBody,
      editedBy: getUser()?._id,
    };
  }
  console.log("newBody", newBody);

  try {
    const init = await addHeader(newBody, method);
    const response = await fetchWithAuthRetry(uri, init);
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}

export async function apiPostPutPublic(body, path, method) {
  const uri = constants.mainUrlPublic + path;
  console.log("oldBody", body);

  let newBody = body;
  if (method === "POST") {
    newBody = {
      ...newBody,
      cluster: getclusterbyDomain(),
    };
  }
  console.log("newBody", newBody);
  try {
    const response = await fetch(uri, await addHeader(newBody, method));
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    // console.log("error", error?.response?.data);
    return {
      body: {
        title: error?.response?.data?.title,
        message: error?.response?.data?.message,
      },
      status,
      error: error?.message,
    };
  }
}

export async function apiPostPutWithoutUrl(body, path, method) {
  const uri = path;

  let newBody = body;

  try {
    const response = await fetch(uri, await addHeader(newBody, method));
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}

export async function logout() {
  try {
    const response = await fetch(
      constants.mainUrl + API_ENDPOINTS.auth.logout,
      await addHeaderWithOutBody("POST"),
    );
    const data = await handleResponse(response);
    return { body: data, status: response.status };
  } catch (error) {
    const status = error?.response?.status || 500;
    return { body: null, status, error: error?.message };
  }
}

async function handleResponse(response) {
  let data = null;
  try {
    data = await response?.json();
  } catch (e) {
    // non-JSON or empty responses
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || "Request failed";
    if (
      response.status === 401 &&
      typeof message === "string" &&
      message.includes("Refresh token")
    ) {
      try {
        refreshTokenEventEmitter.dispatchEvent(
          new CustomEvent("show-refresh-token-popup", {
            detail: { message, status: response.status },
          }),
        );
      } catch (e) {
        // no-op if emitter not available
      }
    }

    const error = new Error(`${message}`);
    error.response = { status: response.status, data };
    throw error;
  }

  return data;
}
